import { VIRTUAL_WIDTH, VIRTUAL_HEIGHT } from './Constants';

/**
 * Handles Keyboard, Mouse (with rock-solid Pointer Lock smoothing), and Touch events
 */
export class InputManager {
  constructor(canvas) {
    this.canvas = canvas;
    this.keys = new Map();
    this.justPressed = new Set();

    this.mouseX = null;
    this.mouseY = null;
    this.mouseDeltaX = 0;
    this.skipDeltas = 0;
    this.mouseSensitivity = 1.0;
    this.isMouseDown = false;
    this.hasMouseMoved = false;
    this.isPointerLocked = false;
    this.enablePointerLock = true;

    this.setupListeners();
  }

  setupListeners() {
    // Keyboard Input
    window.addEventListener('keydown', (e) => {
      if (!this.keys.get(e.code)) {
        this.justPressed.add(e.code);
      }
      this.keys.set(e.code, true);

      // Prevent page scrolling on Arrow keys and Space
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Space'].includes(e.code)) {
        e.preventDefault();
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keys.set(e.code, false);
      this.justPressed.delete(e.code);
    });

    // Pointer Lock Change Events
    const onLockChange = () => {
      this.isPointerLocked = (document.pointerLockElement === this.canvas ||
                              document.mozPointerLockElement === this.canvas ||
                              document.webkitPointerLockElement === this.canvas);
      if (this.isPointerLocked) {
        // Drop the first 2 frames to prevent browser recentering spikes
        this.skipDeltas = 2;
        this.mouseDeltaX = 0;
      }
    };

    document.addEventListener('pointerlockchange', onLockChange);
    document.addEventListener('mozpointerlockchange', onLockChange);
    document.addEventListener('webkitpointerlockchange', onLockChange);

    // Canvas click to request pointer lock
    this.canvas.addEventListener('click', () => {
      if (this.enablePointerLock && !this.isPointerLocked) {
        this.requestPointerLock();
      }
    });

    // Mouse movement with jitter suppression
    window.addEventListener('mousemove', (e) => {
      if (this.isPointerLocked) {
        if (this.skipDeltas > 0) {
          this.skipDeltas--;
          return;
        }
        const dx = e.movementX || e.mozMovementX || e.webkitMovementX || 0;
        // Suppress browser recenter anomalies
        if (Math.abs(dx) < 150) {
          this.mouseDeltaX += dx;
        }
        this.hasMouseMoved = true;
      } else {
        this.updatePointer(e.clientX, e.clientY);
        this.hasMouseMoved = true;
      }
    });

    this.canvas.addEventListener('mousedown', (e) => {
      this.isMouseDown = true;
      if (!this.isPointerLocked) {
        this.updatePointer(e.clientX, e.clientY);
      }
    });

    window.addEventListener('mouseup', () => {
      this.isMouseDown = false;
    });

    // Touch support for mobile/tablets
    this.canvas.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length > 0) {
          const t = e.touches[0];
          this.updatePointer(t.clientX, t.clientY);
          this.hasMouseMoved = true;
        }
        e.preventDefault();
      },
      { passive: false }
    );

    this.canvas.addEventListener(
      'touchstart',
      (e) => {
        this.isMouseDown = true;
        if (e.touches.length > 0) {
          const t = e.touches[0];
          this.updatePointer(t.clientX, t.clientY);
          this.hasMouseMoved = true;
        }
        e.preventDefault();
      },
      { passive: false }
    );

    window.addEventListener('touchend', () => {
      this.isMouseDown = false;
    });
  }

  consumeMouseDeltaX() {
    const dx = this.mouseDeltaX;
    this.mouseDeltaX = 0;
    return dx;
  }

  requestPointerLock() {
    try {
      const lockFn = this.canvas.requestPointerLock ||
                     this.canvas.mozRequestPointerLock ||
                     this.canvas.webkitRequestPointerLock;
      if (lockFn) {
        lockFn.call(this.canvas);
      }
    } catch (e) {
      console.warn('Pointer lock request error', e);
    }
  }

  exitPointerLock() {
    try {
      const exitFn = document.exitPointerLock ||
                     document.mozExitPointerLock ||
                     document.webkitExitPointerLock;
      if (exitFn && this.isPointerLocked) {
        exitFn.call(document);
      }
    } catch (e) {
      console.warn('Pointer lock exit error', e);
    }
  }

  updatePointer(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const scaleX = VIRTUAL_WIDTH / rect.width;
      const scaleY = VIRTUAL_HEIGHT / rect.height;
      this.mouseX = (clientX - rect.left) * scaleX;
      this.mouseY = (clientY - rect.top) * scaleY;
    }
  }

  isKeyDown(code) {
    return !!this.keys.get(code);
  }

  wasKeyJustPressed(code) {
    return this.justPressed.has(code);
  }

  consumeKey(code) {
    const val = this.justPressed.has(code);
    this.justPressed.delete(code);
    return val;
  }

  clearJustPressed() {
    this.justPressed.clear();
  }
}
